import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import {
    BsArrowDown,
    BsChevronDoubleUp,
    BsDownload,
    BsExclamationLg,
    BsFileEarmark,
    BsPrinter,
    BsSafe2,
} from "react-icons/bs";
import { useSelector } from "react-redux";
import { handleInsideClick } from "../../hooks/useOutsideClick";
import { iEmailAddress } from "../../interfaces/emailModel";
import { iGenericComm } from "../../interfaces/iGenericComm";
import { GetMailboxPermissions } from "../../interfaces/Permissions";
import { getAllAttachments, getEmailasDownload } from "../../Logic/GenericComms/CommParts";
import { getLastCommunicationPosition, setLastCommunication } from "../../Logic/LastTouched/comm";
import { formatEmailforEmbed, formatEmailforToOrCC } from "../../Logic/Mail/formatter";
import { RootState, useAppDispatch } from "../../state/store";
import CommIcon from "../Enquiries/communication/commIcon";
import { dateTimeFormat222 } from "../Enquiries/enquiry/enquiry";
import DropDown from "../Generic/DropDown";
import Processing from "../Generic/Processing";
import "./email.scss";
import MainAttachment from "./mainAttachmentItem";

type previewProps = {
    comm: iGenericComm;
    FullWidth: boolean;
    enquirerName: string;
    activeEnquiryId: number;
    uniqVal?: string; //this needs to be removed in future when we refactor the dragging code
};

function Preview(props: previewProps) {
    const frameRef = React.useRef<HTMLIFrameElement>(null);
    const [frameHeight, setFrameHeight] = useState("57px");
    const [showAll, setShowAll] = useState(false);
    const [showDownloadOptions, setshowDownloadOptions] = useState(false);
    const downloadButtonRef = React.useRef<HTMLDivElement>(null);
    const otherPrefiewRef = React.useRef<HTMLDivElement>(null);
    const [body, setBody] = useState("");
    const permissions = useSelector((state: RootState) => state.permissions);
    const [attachments, setAttachments] = useState<Array<{ id: string; name: string; mimeType: string; size: number }>>(
        [],
    );
    const dispatch: React.Dispatch<any> = useAppDispatch();
    const enquiriesCache = useSelector((state: RootState) => state.EnquiryCache.data);
    const enquiries = enquiriesCache.filter(x => props.activeEnquiryId == x.id);
    const mailboxPermissions = GetMailboxPermissions(permissions, enquiries[0].mailbox);

    useEffect(() => {
        (async function callingFunc() {
            const getAddressDisplay = (party: string | iEmailAddress): string => {
                if (typeof party == "string") return party;
                return party.name == undefined ? party.address : party.name + " <" + party.address + ">";
            };

            let sBody = await props.comm.getBody();
            if (sBody == null || sBody == undefined) {
                sBody = "<html><body></body></html>";
            }
            let stylePos = sBody.indexOf("</style>"); // the below code adds the default spacing because some emails do not have spacing between paragraphs and lines, it gets stripped by email clients this is to ensure readability of such emails
            if (stylePos !== -1) {
                sBody = sBody.substring(0, stylePos) + " p {margin:0px;padding:0px;} " + sBody.substring(stylePos);
            }

            // only strip the grey quote border when the email holds our own embed template (its table has a 2px black left border)
            if (/width:\s*100%;\s*border-left:\s*2px\s+solid\s+#000\s*!important;\s*font-size:\s*12px;/i.test(sBody)) {
                sBody = sBody
                    .replace(/border-left:\s*solid\s+#CCCCCC\s+1(?:\.0)?pt\s*;?/gi, "")
                    .replace(/border-left:\s*solid\s+black\s+1\.5pt\s*;?/gi, "");
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(sBody, "text/html");

            const hasLeftBorder = (el: HTMLElement) => {
                const { borderLeftStyle, borderLeftWidth } = el.style;
                return borderLeftStyle !== "" && borderLeftStyle !== "none" && !/^0(px|pt)?$/.test(borderLeftWidth);
            };
            doc.querySelectorAll("table").forEach(t => {
                if (hasLeftBorder(t)) {
                    t.querySelectorAll<HTMLElement>("*").forEach(child => {
                        if (hasLeftBorder(child)) {
                            child.style.borderLeft = "none";
                        }
                    });
                }
            });

            const printDetails = doc.createElement("div");
            printDetails.className = "printHeadersdiv";
            printDetails.style.cssText = "padding:10px 0px 10px 0px;margin-bottom:20px";

            const table = doc.createElement("table");
            table.style.cssText = "border-collapse:collapse;border-spacing:0";

            const addRow = (label: string, value: string, labelWidth?: string) => {
                const tr = doc.createElement("tr");
                const tdLabel = doc.createElement("td");
                tdLabel.style.padding = "0";
                if (labelWidth) {
                    tdLabel.style.width = labelWidth;
                }
                const b = doc.createElement("b");
                b.textContent = label;
                tdLabel.appendChild(b);
                const tdValue = doc.createElement("td");
                tdValue.style.padding = "0";
                tdValue.textContent = value;
                tr.appendChild(tdLabel);
                tr.appendChild(tdValue);
                table.appendChild(tr);
            };

            addRow("From: ", getAddressDisplay(props.comm.from), "80px");
            addRow(
                "To: ",
                typeof props.comm.to == "string" ? props.comm.to : props.comm.to.map(getAddressDisplay).join(", "),
            );
            if (props.comm.cc != null) {
                addRow(
                    "Cc: ",
                    typeof props.comm.cc == "string" ? props.comm.cc : props.comm.cc.map(getAddressDisplay).join(", "),
                );
            }
            addRow("Sent: ", weekday[new Date(props.comm.date).getDay()] + " " + dateTimeFormat222(props.comm.date));
            addRow("Subject: ", props.comm.subject);
            const commAttachments = await props.comm.getAttachments();
            if (props.comm.hasAttachments && commAttachments?.length > 0) {
                addRow("Attachments: ", commAttachments.map(a => a.name).join(", "));
            }
            printDetails.appendChild(table);
            doc.body.insertBefore(printDetails, doc.body.firstChild);

            const style = doc.createElement("style");
            style.textContent = `.printHeadersdiv {display: none;}  @media print {.printHeadersdiv {display: block;border:1px solid #000;border-left: 0px;border-right: 0px;}}`;

            doc.head.appendChild(style);
            if (otherPrefiewRef.current != null) {
                otherPrefiewRef.current!.innerHTML = sBody;
            }
            setAttachments(commAttachments);
            setBody(doc.documentElement.outerHTML);
        })();
        return () => {
            setBody("");
        };
    }, [props.comm.id]);

    let MainAttachmentList;
    let sumValues: number = 0;
    const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // on update
    useEffect(() => {
        return function cleanup() {
            setShowAll(false);
        };
    }, [attachments]);

    useEffect(() => {
        const enqHolder = document.getElementById("enquiryHolder");
        const enquiryH = enqHolder ? parseInt(getComputedStyle(enqHolder, "").height) : 0;
        const commH = parseInt(getComputedStyle(document.getElementById("previewMetaElements")!, "").height);
        let comPreview = document.getElementById("bodyRow" + props.activeEnquiryId + (props.uniqVal ?? ""))!;
        //let frmPreview = document.getElementById("emailBodyiFrame")!;
        comPreview.style.height = window.innerHeight - (enquiryH + commH + 179) + "px";
        window.setTimeout(() => {
            comPreview.scrollTop = getLastCommunicationPosition(props.activeEnquiryId ? props.activeEnquiryId : 0); //+ 200;
            comPreview.scrollLeft = 0;
        }, 50);
        // frmPreview.style.height= window.innerHeight - (enquiryH + commH +70  ) +"px";
        // frmPreview.style.overflow = "auto";
        //comPreview.style.height= "1500px";
    });

    if (attachments.length > 0) {
        attachments.forEach(item => {
            sumValues = sumValues + item.size;
        });

        MainAttachmentList = attachments.map(item => (
            <MainAttachment
                id={item.id}
                name={item.name}
                mimeType={item.mimeType}
                size={item.size}
                key={"mainAttachment11_" + item.id}
            />
        ));
    }

    sumValues = sumValues / 1024;
    let attachmentSizeIndicater: string = "KB";
    if (sumValues > 999.99) {
        sumValues = sumValues / 1024;
        attachmentSizeIndicater = "MB";
    }
    function closePops() {
        closeAttach();
    }

    function closeAttach() {
        var allElements = document.querySelectorAll('*[id^="mAttach-"]');
        allElements.forEach(item => {
            item.className = "MainAttachmentOptionsContainer invisible";
        });
    }

    async function resizeIframe() {
        let ifrm = frameRef.current!;
        if (frameRef != null) {
            const doc = ifrm.contentDocument;
            if (doc) {
                ifrm.style.width = "100%";
                const imageHeight = await getTotalImageHeight(doc, doc.body.scrollWidth!);
                let totalHeight = doc.body.scrollHeight!;
                if (imageHeight > totalHeight) {
                    totalHeight = totalHeight + (imageHeight - doc.body.clientHeight!);
                }
                ifrm.style.height = totalHeight + 24 + "px";
                // set the frame to the available width;
                if (doc.body.scrollWidth) {
                    if (ifrm.scrollWidth < doc.body.scrollWidth) {
                        // check if the content of the email is wider than the iframe then set the frame width to the width of the content
                        ifrm.style.width = doc.body.scrollWidth + "px";
                    }
                }
            }
        }
    }
    function imageHasExplicitSize(doc: Document, img: HTMLImageElement): boolean {
        const widthAttr = img.getAttribute("width");
        const heightAttr = img.getAttribute("height");
        if ((widthAttr && !widthAttr.includes("%")) || (heightAttr && !heightAttr.includes("%"))) {
            return true;
        }

        const computedWidth = doc.defaultView?.getComputedStyle(img).width ?? "";
        return computedWidth.endsWith("px");
    }
    async function getTotalImageHeight(doc: Document, containerWidth: number): Promise<number> {
        // this function calculates the total height of all images in the document. Images with an explicit
        // width/height (attribute or fixed CSS width) use their own rendered width; images left to size
        // themselves off the container (no size set, or width/max-width: 100%) are scaled to containerWidth.
        const images = Array.from(doc.body.querySelectorAll("img"));
        await Promise.all(images.map(img => img.decode().catch(() => {})));
        return images.reduce((sum, img) => {
            if (!img.naturalWidth || !img.naturalHeight) return sum;
            const aspectRatio = img.naturalHeight / img.naturalWidth;
            const width = imageHasExplicitSize(doc, img) ? img.getBoundingClientRect().width : containerWidth;
            return sum + width * aspectRatio;
        }, 0);
    }
    let timer: any = null;
    function saveScrollPos() {
        if (timer !== null) {
            clearTimeout(timer);
        }
        timer = setTimeout(function () {
            setLastCommunication(
                props.activeEnquiryId ? props.activeEnquiryId : 0,
                props.comm ? props.comm.id : 0,
                document.getElementById("bodyRow" + props.activeEnquiryId + (props.uniqVal ?? ""))?.scrollTop!,
            );
        }, 150);
    }

    return (
        <Fragment>
            <div
                className="email-preview"
                style={{ marginLeft: props.FullWidth ? "4px" : "5px" }}
                onClick={() => {
                    closePops();
                }}
            >
                <div className="email-preview--details">
                    <div id="previewMetaElements">
                        <div className="email-preview--details--subject-row">
                            <div
                                className="email-preview--details--subject-row--subject"
                                data-testid="testSubject"
                            >
                                {props.comm.subject}
                            </div>
                            {props.comm.type == "Email" && mailboxPermissions.DownloadMessage == true && (
                                <>
                                    <div
                                        ref={downloadButtonRef}
                                        title="Download Email"
                                        className="icon-button"
                                        style={{
                                            marginLeft: "auto",
                                            marginRight: "0px",
                                            height: "23px",
                                            width: "22px",
                                        }}
                                        onClick={() => {
                                            setshowDownloadOptions(true);
                                        }}
                                    >
                                        <BsDownload
                                            style={{
                                                height: "20px",
                                                width: "18px",
                                                marginTop: "2px",
                                            }}
                                        ></BsDownload>
                                    </div>
                                </>
                            )}
                            <div
                                title="Print Email"
                                className="icon-button"
                                style={{
                                    marginRight: "0px",
                                    marginLeft:
                                        props.comm.type == "Email" && mailboxPermissions.DownloadMessage == true
                                            ? "0px"
                                            : "auto",
                                    height: "23px",
                                    width: "27px",
                                }}
                                onClick={() => {
                                    const printFrame = document.createElement("iframe");
                                    printFrame.style.position = "fixed";
                                    printFrame.style.left = "-9999px";
                                    document.body.appendChild(printFrame);

                                    printFrame.srcdoc = body;

                                    printFrame.onload = () => {
                                        printFrame.contentWindow?.focus();
                                        printFrame.contentWindow?.print();

                                        setTimeout(() => {
                                            document.body.removeChild(printFrame);
                                        }, 1000);
                                    };
                                }}
                            >
                                <BsPrinter
                                    style={{
                                        height: "20px",
                                        width: "18px",
                                        marginTop: "2px",
                                    }}
                                ></BsPrinter>
                            </div>
                            <CommIcon
                                className="icon"
                                type={props.comm.type}
                                unread={props.comm.unread}
                                inBound={props.comm.incoming}
                                title={
                                    props.comm.type === "Email"
                                        ? props.comm.incoming === true
                                            ? "Inbound " + props.comm.type
                                            : "Outbound " + props.comm.type
                                        : props.comm.type
                                }
                            />
                        </div>
                        <div className="email-preview--details--addresses-row">
                            <div
                                className="profile"
                                title={"From: " + props.comm.from}
                            >
                                {userInitials(
                                    typeof props.comm.from == "string" ? props.comm.from : (props.comm.from.name ?? ""),
                                )}
                            </div>
                            <div className="from-to-cc">
                                <div
                                    className="from"
                                    data-testid="testSenderName"
                                >
                                    {typeof props.comm.from == "string"
                                        ? props.comm.from
                                        : formatEmailforEmbed([props.comm.from])}

                                    <div
                                        className="email-preview--details--subject-row--date"
                                        style={{ fontSize: "7pt", float: "right", marginTop: "5px" }}
                                    >
                                        {weekday[new Date(props.comm.date).getDay()]}{" "}
                                        {dateTimeFormat222(props.comm.date)}
                                    </div>
                                </div>
                                <div className="to-cc">
                                    <div className="to">
                                        <div className="label to-label">To:</div>
                                        <div
                                            className="to-addr"
                                            data-testid="testtoAddress"
                                        >
                                            {typeof props.comm.to == "string"
                                                ? props.comm.to
                                                : formatEmailforToOrCC(props.comm.to)}
                                        </div>
                                    </div>
                                    {props.comm.cc != undefined && props.comm.cc.length > 0 && (
                                        <div className="cc">
                                            <div className="label cc-label">Cc:</div>
                                            <div
                                                className="cc-addr"
                                                data-testid="testccAddress"
                                            >
                                                {typeof props.comm.cc == "string"
                                                    ? props.comm.cc
                                                    : formatEmailforToOrCC(props.comm.cc)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="email-preview--details--priority-row">
                            {" "}
                            {props.comm.priority === 1 ? (
                                <BsExclamationLg className="exclamation" />
                            ) : props.comm.priority === 2 ? (
                                <BsExclamationLg className="exclamation" />
                            ) : props.comm.priority === 4 ? (
                                <BsArrowDown className="arrow" />
                            ) : props.comm.priority === 5 ? (
                                <BsArrowDown className="arrow" />
                            ) : (
                                <Fragment />
                            )}
                            <div className="label">
                                {" "}
                                {props.comm.priority === 1 ? (
                                    "Highest Importance"
                                ) : props.comm.priority === 2 ? (
                                    "High Importance"
                                ) : props.comm.priority === 4 ? (
                                    "Low Importance"
                                ) : props.comm.priority === 5 ? (
                                    "Lowest Importance"
                                ) : (
                                    <Fragment />
                                )}
                            </div>
                        </div>
                        {props.comm.hasAttachments && attachments?.length > 0 && (
                            <div className="email-preview--details--attachments-row">
                                <div className="attachments">
                                    {showAll ? MainAttachmentList : MainAttachmentList?.slice(0, 4)}
                                </div>
                                {/* <br style={{ clear: "both", height: "auto" }} className={e.attachments.length > 1 ? "" : "invisible"}></br> */}
                                {attachments?.length > 1 && attachments?.length <= 4 ? (
                                    <div className="attachment-actions less-4">
                                        <div className="total">
                                            {`${attachments?.length} attachments (${sumValues.toFixed(2)} ${attachmentSizeIndicater})`}
                                        </div>
                                        <div
                                            onClick={() => {
                                                getAllAttachments(attachments, "multiple");
                                            }}
                                            className="download-all"
                                        >
                                            <i>
                                                <BsDownload></BsDownload>
                                            </i>
                                            <div className="download-label">Download All</div>
                                        </div>
                                    </div>
                                ) : attachments.length > 4 ? (
                                    <div className="attachment-actions more-4">
                                        <div
                                            className="total"
                                            onClick={() => setShowAll(!showAll)}
                                        >
                                            <i>
                                                <BsChevronDoubleUp
                                                    className={`double-chevron${showAll ? " flipped" : ""}`}
                                                />
                                            </i>
                                            {`${!showAll ? "Show all " : ""}${attachments?.length} attachments (${sumValues.toFixed(2)} ${attachmentSizeIndicater})`}
                                        </div>
                                        <div
                                            onClick={() => {
                                                getAllAttachments(attachments, "multiple");
                                            }}
                                            className="download-all"
                                        >
                                            <i>
                                                <BsDownload></BsDownload>
                                            </i>
                                            <div className="download-label">Download All</div>
                                        </div>
                                    </div>
                                ) : (
                                    []
                                )}
                            </div>
                        )}

                        {/* <div className="email-preview--details--body-row" id="bodyRow">
                                <iframe className='iframe' srcDoc={e.body} id="emailBodyiFrame" onLoad={() => { resizeIframe() }}/>
                            </div> */}
                    </div>
                    <div
                        className="email-preview--details--body-row"
                        id={"bodyRow" + props.activeEnquiryId + (props.uniqVal ?? "")}
                        onScrollCapture={() => {
                            saveScrollPos();
                        }}
                    >
                        {props.comm.type == "Email" && body != "" && (
                            <iframe
                                id="emailBodyiFrame"
                                sandbox="allow-same-origin"
                                srcDoc={body}
                                data-testid="emailBodyiFrame1"
                                scrolling="no"
                                onLoad={e => {
                                    window.setTimeout(() => {
                                        resizeIframe();
                                        // @ts-ignore
                                        const iframeElt: HTMLIFrameElement = e.target;
                                        const iframeDoc =
                                            iframeElt.contentDocument || iframeElt.contentWindow?.document;
                                        iframeDoc?.addEventListener("mousedown", handleInsideClick);
                                    }, 200);
                                }}
                                ref={frameRef}
                            ></iframe>
                        )}

                        {props.comm.type == "Email" && body === "" && <Processing>Getting email body</Processing>}
                        {props.comm.type != "Email" && (
                            <div className="previewMedia">
                                <div
                                    style={{ padding: "2px 20px 5px 20px" }}
                                    ref={otherPrefiewRef}
                                ></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showDownloadOptions && (
                <DropDown
                    top={
                        props.FullWidth
                            ? downloadButtonRef.current
                                ? downloadButtonRef.current.offsetTop + 23
                                : 240
                            : downloadButtonRef.current
                              ? downloadButtonRef.current.offsetTop + 23
                              : 390
                    }
                    left={
                        props.FullWidth
                            ? downloadButtonRef.current
                                ? downloadButtonRef.current.offsetLeft - 180
                                : 650
                            : downloadButtonRef.current
                              ? downloadButtonRef.current.offsetLeft - 180
                              : 680
                    }
                    closeFunction={() => {
                        setshowDownloadOptions(false);
                    }}
                >
                    <div
                        onClick={async () => {
                            await getEmailasDownload(props.comm, "download");
                            setshowDownloadOptions(false);
                        }}
                        className="tlbar-optn"
                    >
                        <BsFileEarmark className="tlbar-optn-icon" />
                        <label className="tlbar-optn-label">Download</label>
                    </div>

                    <div
                        onClick={async () => {
                            await getEmailasDownload(props.comm, "tempStore");
                            setshowDownloadOptions(false);
                        }}
                        className="tlbar-optn"
                    >
                        <BsSafe2 className="tlbar-optn-icon" />
                        <label className="tlbar-optn-label">Download to Temp Store</label>
                    </div>
                </DropDown>
            )}
        </Fragment>
    );
}

export function removeParentheses(name: string): string {
    const openParenIndex = name ? name.indexOf("(") : -1;
    if (openParenIndex == -1) {
        return name;
    }

    let closeParenIndex = openParenIndex;
    let parenthesesCount = 1;

    while (parenthesesCount > 0 && closeParenIndex < name.length - 1) {
        closeParenIndex++;

        if (name.charAt(closeParenIndex) == "(") {
            parenthesesCount++;
        } else if (name.charAt(closeParenIndex) == ")") {
            parenthesesCount--;
        }
    }

    if (parenthesesCount != 0) {
        return name;
    }

    const newName = name.slice(0, openParenIndex) + name.slice(closeParenIndex + 1);
    return removeParentheses(newName.trim()); // rec
}

export const userInitials = (name: string) => {
    const nameStripped = removeParentheses(name);

    const nameSur = nameStripped ? nameStripped.split(" ") : "  ";
    return nameSur[0].charAt(0).toUpperCase() + nameSur[nameSur.length - 1].charAt(0).toUpperCase();
};

export default Preview;
